import "@/index.css";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Play, Instagram, Linkedin, ExternalLink, ArrowRight, GripHorizontal } from "lucide-react";
import { SiteDataProvider, useSiteData } from "../context/SiteDataContext";

// API URLs
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image optimization helper
const getOptimizedImage = (url, width = 400, quality = 75) => {
  if (!url) return url;
  if (url.startsWith('/') || url.includes('localhost')) return url;
  // Skip SVG files and problematic sources
  if (url.toLowerCase().includes('.svg')) return url;
  if (url.includes('wikimedia.org') || url.includes('wikipedia.org')) return url;
  return `${API}/optimize-image?url=${encodeURIComponent(url)}&width=${width}&quality=${quality}`;
};

// NYZA Logo URL
const NYZA_LOGO_URL = "/images/nyza-logo.webp";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// ============================================
// NAVIGATION - Using window.location for reliable navigation
// ============================================
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/references') return location.pathname === '/references';
    if (path === '/') return location.pathname === '/';
    return false;
  };

  // Use window.location for navigation to ensure it works
  const handleNavigation = (href) => {
    window.location.href = href;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4 md:py-6">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="relative z-10">
            <img 
              src={NYZA_LOGO_URL} 
              alt="NYZA Studio" 
              className="h-8 md:h-10"
              style={{ filter: 'brightness(0)' }}
            />
          </a>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center bg-white/90 backdrop-blur-md rounded-full px-2 py-1.5 shadow-md border border-gray-200">
            <a 
              href="/"
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full ${isActive('/') ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              HOME
            </a>
            <a 
              href="/references"
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full ${isActive('/references') ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              REFERENCES
            </a>
            <a 
              href="/#products"
              className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900"
            >
              PRODUCTS
            </a>
            <a 
              href="/#marketing"
              className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900"
            >
              MARKETING
            </a>
          </div>
          
          {/* Contact Button */}
          <a href="/#contact" className="hidden md:block bg-[#9A45FF] text-white text-xs font-medium uppercase tracking-wide px-5 py-2 rounded-full hover:bg-[#8035E8] transition-colors">
            CONTACT
          </a>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-gray-900 bg-white/80 rounded-lg" 
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
            <a 
              href="/" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
            >
              HOME
            </a>
            <a 
              href="/references" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
            >
              REFERENCES
            </a>
            <a 
              href="/#products" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
            >
              PRODUCTS
            </a>
            <a 
              href="/#marketing" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
            >
              MARKETING
            </a>
            <a
              href="/#contact"
              className="block w-full text-center mt-3 bg-[#9A45FF] text-white text-sm font-medium uppercase tracking-wide px-4 py-2 rounded-full"
            >
              CONTACT
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Extract YouTube Video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// YouTube Player Component with pause detection (from working GitHub version)
const YouTubePlayer = ({ videoId, onClose }) => {
  const playerRef = useRef(null);
  const attachRef = useRef(null);
  const iframeId = useRef(`yt-${videoId}-${Date.now()}`).current;

  useEffect(() => {
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    return () => {
      clearInterval(attachRef.current);
      playerRef.current = null;
    };
  }, []);

  const handleIframeLoad = () => {
    attachRef.current = setInterval(() => {
      if (window.YT?.Player && !playerRef.current) {
        clearInterval(attachRef.current);
        try {
          playerRef.current = new window.YT.Player(iframeId, {
            events: {
              onStateChange: (e) => {
                if (e.data === 2 || e.data === 0) onClose();
              },
            },
          });
        } catch (err) {
          console.warn('YT attach failed:', err);
        }
      }
    }, 300);
  };

  return (
    <div className="absolute inset-0 bg-black z-10">
      <iframe
        id={iframeId}
        onLoad={handleIframeLoad}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&playsinline=1&rel=0&modestbranding=1&enablejsapi=1`}
        className="w-full h-full"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        frameBorder="0"
      />
      <button 
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-2 right-2 bg-black/80 text-white p-2 rounded-full hover:bg-purple-600 transition-colors z-20"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Reference Carousel Component - Smooth requestAnimationFrame with GPU acceleration
const ReferenceCarousel = ({ items, title, aspectRatio = "9/16" }) => {
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [playingYoutubeId, setPlayingYoutubeId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showDragHint, setShowDragHint] = useState(false);
  
  // Animation state - drag only
  const [xPos, setXPos] = useState(0);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragStartPos = useRef(0);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getItemWidth = () => {
    if (aspectRatio === "4/5") return isMobile ? 160 : 240;
    return isMobile ? 180 : 280;
  };
  
  const itemWidth = getItemWidth();
  const gap = 16;
  const singleSetWidth = items.length * (itemWidth + gap);

  // No auto-scroll - drag only

  const handlePlayVideo = (uniqueKey, youtubeId) => {
    if (hasDragged.current) return;
    setPlayingVideoId(uniqueKey);
    setPlayingYoutubeId(youtubeId);
  };

  const handleCloseVideo = () => {
    setPlayingVideoId(null);
    setPlayingYoutubeId(null);
  };

  // Mouse events
  const handleMouseEnter = () => {
    setShowDragHint(true);
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    setShowDragHint(false);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragStartPos.current = xPos;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const diff = e.clientX - dragStartX.current;
    if (Math.abs(diff) > 5) hasDragged.current = true;
    
    let newX = dragStartPos.current + diff;
    if (newX > 0) newX = newX % singleSetWidth - singleSetWidth;
    if (newX <= -singleSetWidth) newX = newX % singleSetWidth;
    setXPos(newX);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    setTimeout(() => { hasDragged.current = false; }, 100);
  };

  // Touch events
  const handleTouchStart = (e) => {
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.touches[0].clientX;
    dragStartPos.current = xPos;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const diff = e.touches[0].clientX - dragStartX.current;
    if (Math.abs(diff) > 5) hasDragged.current = true;
    
    let newX = dragStartPos.current + diff;
    if (newX > 0) newX = newX % singleSetWidth - singleSetWidth;
    if (newX <= -singleSetWidth) newX = newX % singleSetWidth;
    setXPos(newX);
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    setTimeout(() => { hasDragged.current = false; }, 100);
  };

  if (!items || items.length === 0) return null;

  const displayItems = [...items, ...items, ...items];

  return (
    <div className="mb-16">
      <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-gray-900">{title}</h3>

      <div 
        className={`relative overflow-hidden select-none ${isDragging.current ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag hint overlay */}
        {!isMobile && showDragHint && !playingVideoId && !isDragging.current && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium shadow-lg border border-purple-200">
              <GripHorizontal className="w-4 h-4 text-purple-600" />
              Drag to scroll
            </div>
          </div>
        )}

        <div 
          className="flex"
          style={{ 
            gap: `${gap}px`,
            width: 'fit-content',
            transform: `translate3d(${xPos}px, 0, 0)`,
            willChange: 'transform'
          }}
        >
          {displayItems.map((post, index) => {
            const videoId = getYouTubeVideoId(post.youtube_url);
            const uniqueKey = `${post.id}-${index}`;
            const isPlaying = playingVideoId === uniqueKey;
            const hasVideo = post.is_video && videoId;
            
            return (
              <div 
                key={uniqueKey}
                className="flex-shrink-0 group"
                style={{ width: `${itemWidth}px` }}
                onClick={() => {
                  if (!isPlaying && !hasDragged.current && hasVideo) {
                    handlePlayVideo(uniqueKey, videoId);
                  }
                }}
              >
                <div 
                  className={`rounded-2xl overflow-hidden relative bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-purple-300 ${hasVideo ? 'cursor-pointer' : ''}`}
                  style={{ aspectRatio }}
                >
                  <img 
                    src={getOptimizedImage(post.image, 350, 75)} 
                    alt={post.profile} 
                    className={`w-full h-full object-cover transition-transform duration-700 ${isPlaying ? 'opacity-0' : 'group-hover:scale-105'}`} 
                    draggable={false}
                    loading="lazy"
                  />
                  
                  {isPlaying && playingYoutubeId && (
                    <YouTubePlayer 
                      videoId={playingYoutubeId} 
                      onClose={handleCloseVideo}
                    />
                  )}
                  
                  {!isPlaying && (
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="reference-badge">
                        <div className="reference-badge-profile">
                          <img src={getOptimizedImage(post.logo, 60, 80)} alt={post.profile} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="reference-badge-info flex-1">
                          <div className="reference-badge-name">
                            @{post.profile}
                            {post.verified && (
                              <svg className="w-3 h-3 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                            )}
                          </div>
                          <div className="reference-badge-followers">{post.followers} · {post.category}</div>
                        </div>
                        {hasVideo && (
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (!hasDragged.current) handlePlayVideo(uniqueKey, videoId);
                            }}
                            className="reference-badge-play"
                          >
                            <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Website Projects Section
const WebsiteProjectsSection = () => {
  const { projects, content, pages } = useSiteData();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [touchedIndex, setTouchedIndex] = useState(null);

  const handleTouch = (index) => {
    setTouchedIndex(touchedIndex === index ? null : index);
  };

  const getLinkedPageSlug = (project) => {
    if (!project.linked_page_id || !pages) return null;
    const linkedPage = pages.find(p => p.id === project.linked_page_id);
    return linkedPage?.slug || null;
  };

  if (!projects || projects.length === 0) return null;

  return (
    <section className="py-12 relative z-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{content?.portfolio_websites_title || "Website Projects"}</h3>
          <p className="text-gray-500 hidden md:block">{content?.portfolio_websites_subtitle || "Hover to scroll through our work"}</p>
          <p className="text-gray-500 md:hidden">Tap to scroll through our work</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((site, index) => {
            const linkedPageSlug = getLinkedPageSlug(site);
            
            return (
              <div 
                key={site.id || index} 
                className="group" 
                onMouseEnter={() => setHoveredIndex(index)} 
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="card-light overflow-hidden">
                  <div 
                    className="aspect-video overflow-hidden relative cursor-pointer"
                    onClick={() => handleTouch(index)}
                  >
                    <motion.img 
                      src={site.image} 
                      alt={site.title} 
                      className="w-full object-cover object-top" 
                      style={{ height: '400%', minHeight: '400%' }} 
                      animate={{ y: (hoveredIndex === index || touchedIndex === index) ? '-75%' : '0%' }} 
                      transition={{ duration: 5, ease: 'easeInOut' }} 
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-xs text-purple-600 uppercase tracking-wider font-medium">{site.category}</span>
                        <h4 className="text-lg font-bold text-gray-900 mt-1">{site.title}</h4>
                      </div>
                      <div className="hidden md:flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full">
                        <svg className="w-4 h-4 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 19V5M5 12l7-7 7 7"/>
                        </svg>
                        <span className="text-xs text-gray-500 font-medium">Hover</span>
                      </div>
                      <div className="md:hidden flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full" onClick={() => handleTouch(index)}>
                        <span className="text-sm">👆</span>
                        <span className="text-xs text-gray-500 font-medium">Touch</span>
                      </div>
                    </div>
                    
                    {linkedPageSlug && (
                      <a 
                        href={`/p/${linkedPageSlug}`}
                        onClick={(e) => { e.preventDefault(); window.location.href = `/p/${linkedPageSlug}`; }}
                        className="btn-primary w-full py-2.5 text-center text-sm flex items-center justify-center gap-2 mt-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Click for more infos</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Footer - Light Theme
const Footer = () => {
  const { content } = useSiteData();
  
  return (
    <footer className="py-8 bg-white border-t border-gray-100">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} NYZA Studio</p>
          
          <div className="flex items-center gap-4">
            {content?.footer_instagram && (
              <a href={content.footer_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600 transition-colors uppercase text-sm font-medium">
                INSTAGRAM
              </a>
            )}
            {content?.footer_linkedin && (
              <a href={content.footer_linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600 transition-colors uppercase text-sm font-medium">
                LINKEDIN
              </a>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm">
            <a href="/impressum" className="text-gray-500 hover:text-purple-600 transition-colors uppercase font-medium">IMPRINT</a>
            <a href="/agb" className="text-gray-500 hover:text-purple-600 transition-colors uppercase font-medium">AGBS</a>
            <a href="/datenschutz" className="text-gray-500 hover:text-purple-600 transition-colors uppercase font-medium">PRIVACY POLICY</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main References Page Content - Light Theme with Purple Blur
const ReferencesContent = () => {
  const { references, loading } = useSiteData();
  
  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full"
      />
    </div>
  );

  // Group references by category
  const categories = references.reduce((acc, ref) => {
    const cat = ref.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ref);
    return acc;
  }, {});

  // Define category order and their aspect ratios
  const categoryConfig = [
    { name: 'CGI Videos', aspectRatio: '9/16' },
    { name: '2D Animations', aspectRatio: '9/16' },
    { name: 'Video Productions', aspectRatio: '9/16' },
    { name: '3D Animations', aspectRatio: '9/16' },
    { name: 'Graphics', aspectRatio: '4/5' },
    { name: 'Photos', aspectRatio: '9/16' },
    { name: 'Stories', aspectRatio: '9/16' },
  ];

  return (
    <div className="bg-white min-h-screen relative overflow-x-hidden">
      <ScrollToTop />
      
      {/* Purple Blur Circles distributed throughout the page */}
      <div className="absolute top-0 left-[-10%] w-[600px] h-[600px] rounded-full bg-purple-300/40 blur-[180px] pointer-events-none" />
      <div className="absolute top-[15%] right-[-15%] w-[700px] h-[700px] rounded-full bg-purple-400/30 blur-[200px] pointer-events-none" />
      <div className="absolute top-[35%] left-[-5%] w-[500px] h-[500px] rounded-full bg-purple-300/35 blur-[150px] pointer-events-none" />
      <div className="absolute top-[55%] right-[-10%] w-[600px] h-[600px] rounded-full bg-purple-400/25 blur-[180px] pointer-events-none" />
      <div className="absolute top-[75%] left-[10%] w-[550px] h-[550px] rounded-full bg-purple-300/30 blur-[160px] pointer-events-none" />
      <div className="absolute bottom-[5%] right-[5%] w-[500px] h-[500px] rounded-full bg-purple-400/35 blur-[150px] pointer-events-none" />
      
      <Navigation />
      
      {/* Hero Section - More spacing from header */}
      <section className="pt-44 md:pt-52 pb-16 relative z-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="text-4xl md:text-5xl font-bold mb-6 text-gray-900"
          >
            REFERENCES
          </motion.h1>
          <motion.h2 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="text-xl md:text-2xl font-bold text-gray-700 mb-4"
          >
            Your project could be next—are you ready?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }} 
            className="text-gray-500 max-w-2xl mx-auto"
          >
            Check out my references to see the projects I've been part of — real work, real results, real impact.
          </motion.p>
        </div>
      </section>

      {/* Website Projects Section */}
      <WebsiteProjectsSection />

      {/* Reference Categories */}
      <section className="py-12 relative z-10">
        <div className="max-w-6xl mx-auto px-4">
          {categoryConfig.map((config) => {
            const items = categories[config.name];
            if (!items || items.length === 0) return null;
            return (
              <ReferenceCarousel 
                key={config.name} 
                items={items} 
                title={config.name}
                aspectRatio={config.aspectRatio}
              />
            );
          })}
          {Object.keys(categories).length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500">No references yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default function ReferencesPage() {
  return (
    <SiteDataProvider>
      <ReferencesContent />
    </SiteDataProvider>
  );
}
